import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { TrendingUp, TrendingDown, Calendar, Activity, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import FieldHelp from "@/components/FieldHelp";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function BudgetPage() {
  const [b, setB] = useState({ total_budget: 0, plan_start: "", plan_end: "" });
  const [data, setData] = useState(null);
  const upd = (k) => (e) => setB((x) => ({ ...x, [k]: e?.target?.value ?? e }));

  const load = async () => {
    const { data } = await api.get("/dashboard/budget");
    setData(data);
    setB({ total_budget: data.total_budget, plan_start: data.plan_start, plan_end: data.plan_end });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.put("/dashboard/budget", { ...b, total_budget: parseFloat(b.total_budget) || 0 }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="budget-page">
      <header>
        <h1 className="text-4xl font-display font-black tracking-tight">NDIS Budget</h1>
        <p className="text-muted-foreground">Set your plan total and dates to see live utilisation and projections.</p>
      </header>

      {data && (
        <Card className="rounded-3xl p-6 border-2 space-y-4" data-testid="budget-overview">
          <div className="flex items-baseline justify-between">
            <div className="font-display font-black text-4xl">${data.spent.toLocaleString()}<span className="text-lg text-muted-foreground"> / ${data.total_budget.toLocaleString()}</span></div>
            <div className="text-right text-xs uppercase tracking-wider font-bold text-muted-foreground">{data.pct_used}% used</div>
          </div>
          <Progress value={Math.min(data.pct_used, 100)} className="h-4 rounded-full" data-testid="budget-progress-detail" />
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Stat label="Remaining" value={`$${data.remaining.toLocaleString()}`} accent="success" />
            <Stat label="Pending payments" value={`$${data.pending.toLocaleString()}`} accent="coral" />
            <Stat label="Plan window" value={data.plan_start && data.plan_end ? `${data.plan_start} → ${data.plan_end}` : "—"} small />
          </div>
          {data.projection && <ProjectionCard p={data.projection} totalBudget={data.total_budget} spent={data.spent} pctUsed={data.pct_used} />}
        </Card>
      )}

      <Card className="rounded-3xl p-6 border-2 space-y-3" data-testid="budget-form">
        <h2 className="font-display font-extrabold text-2xl">Update plan</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <F label="Total budget ($)" v={b.total_budget} oc={upd("total_budget")} type="number" id="total-budget"
             help={{ text: "Your total NDIS plan amount in AUD — find this on your most recent NDIS plan letter.", learnMoreId: "ndis-budget" }} />
          <F label="Plan start" v={b.plan_start} oc={upd("plan_start")} type="date" id="plan-start"
             help={{ text: "The first day of your current NDIS plan.", learnMoreId: "ndis-budget" }} />
          <F label="Plan end" v={b.plan_end} oc={upd("plan_end")} type="date" id="plan-end"
             help={{ text: "Last day of your current plan. Used for the live burn-rate projection on the dashboard.", learnMoreId: "ndis-budget" }} />
        </div>
        <Button onClick={save} className="rounded-full font-bold" style={{ background: "hsl(var(--client))" }} data-testid="save-budget-btn">Save</Button>
      </Card>
    </div>
  );
}

function Stat({ label, value, accent, small }) {
  return (
    <div className="rounded-2xl border bg-muted/30 p-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className={`font-display font-black ${small ? "text-sm" : "text-2xl"} ${accent === "coral" ? "text-coral" : accent === "success" ? "text-emerald-700" : ""}`}>{value}</div>
    </div>
  );
}

function F({ label, v, oc, type = "text", id, help }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase font-bold tracking-wider flex items-center">
        {label}
        {help && <FieldHelp text={help.text} learnMoreId={help.learnMoreId} />}
      </Label>
      <Input value={v} onChange={oc} type={type} className="rounded-xl min-h-[48px]" data-testid={`field-${id}`} />
    </div>
  );
}

function ProjectionCard({ p, totalBudget, spent, pctUsed }) {
  const overspend = p.projected_overspend || 0;
  const onTrack = Math.abs(overspend) < (totalBudget * 0.05); // ±5% tolerance
  const overspending = overspend > 0 && !onTrack;
  const underspending = overspend < 0 && !onTrack;

  // status pill
  let pillBg = "bg-emerald-500", pillText = "On track", PillIcon = CheckCircle2;
  if (p.status === "upcoming") { pillBg = "bg-sky-500"; pillText = "Upcoming"; PillIcon = Calendar; }
  else if (p.status === "closed") { pillBg = "bg-slate-500"; pillText = "Plan ended"; PillIcon = Calendar; }
  else if (overspending) { pillBg = "bg-destructive"; pillText = "Overspending"; PillIcon = AlertTriangle; }
  else if (underspending) { pillBg = "bg-amber-500"; pillText = "Underspending"; PillIcon = TrendingDown; }

  // pace: compares spend% vs time%
  const spendPct = pctUsed || 0;
  const timePct = p.time_pct || 0;
  const paceDiff = (spendPct - timePct).toFixed(1);

  return (
    <div className={`rounded-2xl p-5 border-2 space-y-4 ${
      overspending ? "border-destructive/40 bg-destructive/5" :
      underspending ? "border-amber-300 bg-amber-50" :
      p.status === "upcoming" ? "border-sky-200 bg-sky-50" :
      "border-emerald-300 bg-emerald-50"
    }`} data-testid="budget-projection-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider text-white ${pillBg}`} data-testid="projection-status-pill">
              <PillIcon className="w-3 h-3" /> {pillText}
            </span>
            <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Plan-window projection</span>
          </div>
          <h3 className="font-display font-extrabold text-2xl mt-2">
            Projected end-of-plan spend: <span className="text-client">{fmt(p.projected_end_spend)}</span>
          </h3>
          <p className="text-sm mt-1">
            {overspending && <span className="text-destructive font-bold">Likely to overspend by {fmt(overspend)}.</span>}
            {underspending && <span className="text-amber-700 font-bold">Likely to underspend by {fmt(-overspend)}.</span>}
            {!overspending && !underspending && p.status === "active" && <span className="text-emerald-700 font-bold">On track to finish within budget.</span>}
            {p.status === "upcoming" && <span className="text-sky-700 font-bold">Plan starts on {p.days_remaining} day{p.days_remaining === 1 ? "" : "s"}.</span>}
            {p.status === "closed" && <span className="text-slate-700 font-bold">Plan window has ended.</span>}
          </p>
        </div>
      </div>

      {/* Time vs Spend dual progress */}
      <div className="space-y-2.5" data-testid="projection-pace">
        <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider">
          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Time elapsed</span>
          <span>{p.days_elapsed} of {p.total_days} days · {timePct.toFixed(1)}%</span>
        </div>
        <Progress value={Math.min(timePct, 100)} className="h-2.5 rounded-full" />
        <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider mt-1.5">
          <span className="flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Budget spent</span>
          <span>{fmt(spent)} · {spendPct.toFixed(1)}%</span>
        </div>
        <Progress value={Math.min(spendPct, 100)} className="h-2.5 rounded-full" />
        {p.status === "active" && (
          <p className="text-xs text-muted-foreground pt-1">
            {paceDiff > 0
              ? <><TrendingUp className="w-3 h-3 inline mr-1" /> Spending <b>{paceDiff}%</b> ahead of time elapsed.</>
              : paceDiff < 0
              ? <><TrendingDown className="w-3 h-3 inline mr-1" /> Spending <b>{(-paceDiff).toFixed(1)}%</b> behind time elapsed — under-utilising.</>
              : <>Spend perfectly matches time elapsed.</>}
          </p>
        )}
      </div>

      {/* Three quick metrics */}
      <div className="grid grid-cols-3 gap-2">
        <MiniStat label="Days remaining" value={p.days_remaining} testId="proj-days-remaining" />
        <MiniStat label="Daily burn" value={fmt(p.daily_rate)} testId="proj-daily-rate" />
        <MiniStat label="Ideal / day" value={fmt(p.ideal_daily_rate)} testId="proj-ideal-rate" />
      </div>
    </div>
  );
}

function MiniStat({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-background/70 border-2 p-2.5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className="font-display font-black text-lg text-foreground">{value}</div>
    </div>
  );
}
